import { Component, DestroyRef, OnDestroy, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { IClient, IClients } from 'src/app/shared/types/shared-state';
import { CreateManageUserAsync } from 'src/app/store/createManageUser/async';
import {
  errorsFetchingClients,
  fetchingClients,
  getClientsOfAdmin,
} from 'src/app/store/createManageUser/selector';
import {
  IAdminAndUserAuthResponse,
  ICreateManageUserState,
  IManageuser,
  IAdminAndUserAuthsReq,
  IManageUserAuthsReq,
  initialCreateManageUserState
} from 'src/app/store/createManageUser/state';
import { Lux } from '@roosevelt/common-ui-lib/core';
import {
  createAllGroupsAuthorizationForm,
  isDelegateUser,
  refreshDirtyStateFromSnapshot,
  syncNestedUserAuthControl
} from '../manage-auth-form.util';
import { ManageAuthSharedBase } from '../manage-auth-shared.base';

@Component({
    selector: 'bmt-manage-auth-at-multi-group',
    templateUrl: './multi-group-level.component.html',
    styleUrls: ['./multi-group-level.component.scss'],
    standalone: false
})
export class ManageAuthAtMutiGroupComponent extends ManageAuthSharedBase implements OnDestroy {
  readonly userInfo = input.required<IManageuser>();
  readonly cancelGroupAuthEdit = output<boolean>();
  private readonly destroyRef = inject(DestroyRef);

  private readonly errorsFetchingClientsState = toSignal(this.lux.get(errorsFetchingClients, this.luxId), { initialValue: '' });
  private readonly fetchingClientsState = toSignal(this.lux.get(fetchingClients, this.luxId), { initialValue: false });
  private readonly getClientsOfAdminState = toSignal(this.lux.get(getClientsOfAdmin, this.luxId), {
    initialValue: { clients: [], pagination: { totalRecords: 0, limit: 0, offset: 0 }, sort: [] } as IClients
  });
  private readonly selectedClientState = signal<IClient | null>(null);
  private readonly adminClientsInfoState = signal<IClient[]>([]);
  private readonly alreadyUserState = signal(false);
  private readonly authsModifiedForAllGroupsState = signal(false);
  private readonly totalRecordsState = signal(0);
  private readonly userInfoForPopUpState = signal<IManageuser | null>(null);

  editAuthorizations = false;

  forNewlyCreatedUser = [{
    eligibilityAccessLevel: '',
    clientKnowledgeIndicator: false,
    billingReportAccessIndicator: false,
    customClientReportAccessIndicator: false,
    claimDetailReportAccessIndicator: false,
    satAccessLevel: '',
    satAccessReportIndicator: false
  }];

  editAllGroupsForm: UntypedFormGroup;
  initialSnapshot: any;

  defaultPageSize = 25;
  selectedIndex = 0;
  adminOnly = ['BMT-AGENCY-ADMIN', 'BMT-CLIENT-ASSOCIATION-ADMIN', 'BMT-AGENT-ADMIN'];

  constructor(private readonly fb: UntypedFormBuilder,
              private readonly router: Router,
              lux: Lux<{ createManageUserState: ICreateManageUserState }>,
              private readonly createManageUserAsync: CreateManageUserAsync) {
    super(lux, 'BMT-manage-auth-at-multi-group' + ManageAuthAtMutiGroupComponent.name + new Date().toISOString());
    effect(() => {
      this.alreadyUserState.set(this.userInfo().selectedUserType.toLowerCase() !== 'delegate');
    });

    effect(() => {
      this.totalRecordsState.set(this.getClientsOfAdminState()?.pagination?.totalRecords || 0);
      this.adminClientsInfoState.set(this.getClientsOfAdminState()?.clients || []);
    });

    effect(() => {
      const selectedOption = this.selectedOptionState();
      const adminAndUserAuths = this.userAndAdminAuthsState();

      this.editAllGroupsForm = null;
      this.authsModifiedForAllGroupsState.set(false);

      if (selectedOption === 'allGroups' && adminAndUserAuths.userRelatedAuthorizations.length) {
        this.createEditAllGorupsForm();
      }
    });
  }

  get selectedClient(): IClient | null {
    return this.selectedClientState();
  }

  set selectedClient(value: IClient | null) {
    if (!value) {
      this.userInfoForPopUpState.set(null);
    }
    this.selectedClientState.set(value);
  }

  get adminClientsInfo(): IClient[] {
    return this.adminClientsInfoState();
  }

  get updatingAuths(): boolean {
    return this.selectedOptionState() && this.selectedOptionState() !== 'customizeGroups'
      ? this.updatingAuthInProgressState()
      : false;
  }

  get totalRecords(): number {
    return this.totalRecordsState();
  }

  get authsModifiedForAllGroups(): boolean {
    return this.authsModifiedForAllGroupsState();
  }

  get errorsFetchingClients(): string {
    return this.errorsFetchingClientsState();
  }

  get fetchingClients(): boolean {
    return this.fetchingClientsState();
  }

  get alreadyUser(): boolean {
    return this.alreadyUserState();
  }

  set alreadyUser(value: boolean) {
    this.alreadyUserState.set(value);
  }

  get userInfoForPopUp(): IManageuser {
    return this.userInfoForPopUpState() ?? this.userInfo();
  }

  protected get fallbackUserAuthorizations() {
    return this.forNewlyCreatedUser;
  }

  protected get initialAdminUserAuthResponse(): IAdminAndUserAuthResponse {
    return initialCreateManageUserState.adminAndUserAuths.authResponse;
  }

  protected get managedUserInfo(): IManageuser {
    return this.userInfo();
  }

  protected onManageUserAuthSuccess(selectedOption: string): void {
    this.authsModifiedForAllGroupsState.set(false);
    this.alreadyUserState.set(selectedOption !== 'delegate');
  }

  ngOnDestroy(): void {
    this.destroyManageUserState();
  }

  fetchUserAuthorizations(): void {
    const userInfo = this.userInfo();
    const userContextStr = userInfo.contextOfAdmin.split('/');
    const adminAndUserAuthReq: IAdminAndUserAuthsReq = {
      userIdentifier: userInfo.selectedUserId,
      payer: userContextStr[1],
      payerAdmin: userContextStr[0],
      groupAccessLevel: 'all',
      groupSpecifiedIdentifier: userContextStr[2],
      ...userContextStr[3] && {subGroupSpecifiedIdentifier: userContextStr[3]}
    };
    this.lux.set(state => state.createManageUserState.adminAndUserAuths, {authRequest: adminAndUserAuthReq});
    this.createManageUserAsync.getAdminAndUserAuths('edit')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  manageUserAuthFor(option) {
    this.clearManageUserAuthFeedback();
    this.selectedOptionState.set(option);
    if (option === 'customizeGroups') {
      this.getClients(0);
    }

    if (option === 'allGroups') {
      this.fetchUserAuthorizations();
      return;
    }

    this.clearAdminAndUserAuthState();
  }

  toggleTheClient(client: IClient) {
    this.userInfoForPopUpState.set({
      ...this.userInfo(),
      contextOfAdmin: this.userInfo().contextOfAdmin.split('/')[0] + '/' + client.planAcronym + '/' + client.specifiedId
    });
    this.selectedClient = client;
  }

  navigateToUserAdmin() {
    this.clearManageUserAuthFeedback();
    this.router.navigate(['userAdmin/manage']);
  }

  createEditAllGorupsForm() {
    const { editForm, initialSnapshot } = createAllGroupsAuthorizationForm(
      this.fb,
      this.userAndAdminAuths.adminRelatedAuthorizations,
      this.userAndAdminAuths.userRelatedAuthorizations[0]
    );
    this.editAllGroupsForm = editForm;
    this.initialSnapshot = initialSnapshot;
    this.authsModifiedForAllGroupsState.set(false);
  }

  manageAuthorizations() {
    const manageUserReq: IManageUserAuthsReq = {
      userIdentifier: this.userInfo().selectedUserId,
      userType: this.selectedOption !== 'delegate' ? 'user' : 'delegate'
    };
    if (this.selectedOption === 'allGroups') {
      const authFormValues = (this.editAllGroupsForm.controls.userAuthForm as UntypedFormGroup).getRawValue();
      manageUserReq.authorizations = [
        {
          payers: this.userInfo().planAcronyms,
          groupAccessLevel: 'all',
          subgroupAuthorizations: [
            {
              eligibilityAccessLevel: authFormValues.userEligAccess,
              satAccessLevel: authFormValues.userSatAccess,
              clientKnowledgeIndicator: authFormValues.userClientKnowledge,
              customClientReportAccessIndicator: authFormValues.userCustomClientReportAccess,
              billingReportAccessIndicator: authFormValues.userBillingReport,
              claimDetailReportAccessIndicator: authFormValues.userClientDetailReport
            }
          ]
        }
      ];
    }
    this.triggerUserAuthModify(manageUserReq);
  }

  triggerUserAuthModify(manageUserReq: IManageUserAuthsReq): void {
    this.prepareManageUserAuthSubmission();
    this.lux.set(state => state.createManageUserState.manageUserAuths, {authRequest: manageUserReq});
    this.createManageUserAsync.manageUserAuthorizations()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.completeManageUserAuthSubmission();
      });
  }

  getClients(offset: number): void {
    const req = {
      planAcronym: this.userInfo().planAcronyms,
      offset: offset,
      limit: this.defaultPageSize
    };
    this.createManageUserAsync.getClients(req)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.errorsFetchingClientsState()) {
          return;
        }

        const element = document.querySelector('#scrollId');
        element?.scrollIntoView();
      });
  }

  getMoreClients(page) {
    this.getClients(page.pageIndex >= 1 ? (page.pageIndex * this.defaultPageSize) : 0);
  }

  syncAllGroupValue(adminControlName: string, userControlName: string, requireTruthy = false): void {
    syncNestedUserAuthControl(
      this.editAllGroupsForm,
      adminControlName,
      userControlName,
      () => this.refreshAllGroupDirtyState(),
      requireTruthy
    );
  }

  refreshAllGroupDirtyState(): void {
    refreshDirtyStateFromSnapshot(
      this.editAllGroupsForm?.controls?.userAuthForm as UntypedFormGroup | undefined,
      value => this.authsModifiedForAllGroupsState.set(value),
      this.initialSnapshot,
      isDelegateUser(this.userInfo().selectedUserType)
    );
  }

  viewUpdatedAuthorizations(): void {
    this.clearManageUserAuthFeedback();
    this.cancelGroupAuthEdit.emit(true);
  }
}
